/**
 * Layout — App shell. Composes AppHeader (sticky top) + page content +
 * MobileCartBar (sticky bottom on mobile) + BottomNav (mobile tab bar) +
 * Footer.
 *
 * The header / search / location chip / cart pill / theme toggle all live in
 * AppHeader (components/shell/AppHeader.tsx) so this file stays slim and is
 * responsible only for vertical composition + the page <main>.
 */
import React from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import AppHeader from './shell/AppHeader';
import BottomNav from './shell/BottomNav';
import MobileCartBar from './MobileCartBar';
import 'bootstrap/dist/css/bootstrap.min.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <div className="d-flex flex-column min-vh-100">
      <AppHeader />

      {/*
        Main Content — fluid Container (no max-width cap) so wide monitors
        render edge-to-edge. Inner pages that want a narrower cap nest a
        regular <Container style={{ maxWidth: 1200 }}> inside this.

        The `pm-has-bottom-nav` class adds the safe-area-aware bottom padding
        on mobile so the BottomNav never covers content.
      */}
      <main className="flex-grow-1 py-3 pm-main pm-has-bottom-nav">
        <Container fluid className="px-3 px-md-4">
          {children}
        </Container>
      </main>

      {/* Sticky bottom cart bar — mobile only, auto-hides on cart/checkout/admin. */}
      <MobileCartBar />

      {/* Persistent mobile tab bar — only renders < 768px. */}
      <BottomNav />

      <Footer />
    </div>
  );
};

const Footer: React.FC = () => (
  <footer className="bg-dark text-white mt-5 py-4">
    <Container>
      <div className="row">
        <div className="col-md-4 mb-3">
          <div className="mb-2">
            <Logo size={64} invert tagline="Fresh groceries · Fast delivery · Local stores" />
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3 small text-white-50">
            <span>🔒 Secure checkout</span>
            <span>🌿 Freshness guarantee</span>
            <span>↩ Easy returns</span>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <h6 className="fw-bold" style={{ color: '#86EFAC' }}>Quick Links</h6>
          <ul className="list-unstyled small">
            <li><Link to="/" className="text-white-50 text-decoration-none">Home</Link></li>
            <li><Link to="/stores" className="text-white-50 text-decoration-none">Browse Stores</Link></li>
            <li><Link to="/restaurants" className="text-white-50 text-decoration-none">Order Food</Link></li>
            <li><Link to="/orders" className="text-white-50 text-decoration-none">Track Order</Link></li>
          </ul>
        </div>
        <div className="col-md-4 mb-3">
          <h6 className="fw-bold" style={{ color: '#86EFAC' }}>Help & Support</h6>
          <p className="small text-white-50 mb-1">Email: support@provisionmart.com</p>
          <p className="small text-white-50 mb-1">Phone: +91-1800-SHOP-123</p>
          <p className="small text-white-50">Open 8 AM – 10 PM · 7 days a week</p>
        </div>
      </div>
      <hr className="bg-secondary" />
      <p className="text-center text-white-50 mb-0 small">
        © {new Date().getFullYear()} ProvisionMart. Fresh groceries from your neighbourhood, delivered fast.
      </p>
    </Container>
  </footer>
);

export default Layout;

